import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Calculator, Loader2, DollarSign, Clock, FileCode, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVAILABLE_FEATURES = [
  { id: 'authentication', label: 'User Authentication', price: 500 },
  { id: 'user_management', label: 'User Management', price: 400 },
  { id: 'payment_integration', label: 'Payment Integration', price: 800 },
  { id: 'database_design', label: 'Database Design', price: 600 },
  { id: 'api_development', label: 'API Development', price: 700 },
  { id: 'admin_dashboard', label: 'Admin Dashboard', price: 900 },
  { id: 'mobile_responsive', label: 'Mobile Responsive', price: 400 },
  { id: 'real_time_features', label: 'Real-time Features', price: 1000 },
  { id: 'file_upload', label: 'File Upload System', price: 300 },
  { id: 'email_notifications', label: 'Email Notifications', price: 250 },
  { id: 'analytics_dashboard', label: 'Analytics Dashboard', price: 700 },
  { id: 'third_party_integrations', label: 'Third-party Integrations', price: 500 },
  { id: 'custom_design', label: 'Custom UI/UX Design', price: 1200 },
  { id: 'security_features', label: 'Advanced Security', price: 800 },
  { id: 'search_functionality', label: 'Search Functionality', price: 400 },
  { id: 'social_features', label: 'Social Features', price: 600 },
];

const PROJECT_TYPES = [
  'Web Application',
  'Mobile App',
  'E-commerce Platform',
  'SaaS Product',
  'Enterprise Software',
  'API/Backend Service',
  'Custom CRM/ERP',
  'Other'
];

export default function Estimator() {
  const [formData, setFormData] = useState({
    project_name: '',
    project_type: '',
    features: [],
    description: '',
    timeline: 'normal'
  });
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);

  const handleFeatureToggle = (featureId) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...prev.features, featureId]
    }));
  };

  const calculatePreviewPrice = () => {
    let base = 1000;
    formData.features.forEach(featureId => {
      const feature = AVAILABLE_FEATURES.find(f => f.id === featureId);
      if (feature) base += feature.price;
    });
    
    const multipliers = { urgent: 1.5, normal: 1.0, flexible: 0.9 };
    return base * (multipliers[formData.timeline] || 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_name || !formData.project_type || formData.features.length === 0) {
      toast.error('Please fill in all required fields and select at least one feature');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/estimate`, {
        ...formData,
        features: formData.features.map(id => 
          AVAILABLE_FEATURES.find(f => f.id === id)?.label || id
        )
      });
      setEstimate(response.data);
      toast.success('Estimate generated successfully!');
    } catch (error) {
      console.error('Estimation failed:', error);
      toast.error('Failed to generate estimate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-gray-950 to-gray-950 -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-green-500/20 text-green-400 border-green-500/30">
            <Sparkles className="w-3 h-3 mr-1" />
            AI-Powered Estimation
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Outfit']">
            Project <span className="text-gradient">Estimator</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Get an instant price estimate with AI-generated design structure. Free, no commitment required.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form */}
          <Card className="bg-gray-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Outfit']">
                <Calculator className="w-5 h-5 text-blue-400" />
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="project_name">Project Name *</Label>
                  <Input
                    id="project_name"
                    placeholder="My Awesome Project"
                    value={formData.project_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, project_name: e.target.value }))}
                    className="bg-gray-800/50 border-gray-700 font-mono"
                    data-testid="estimator-project-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="project_type">Project Type *</Label>
                  <Select
                    value={formData.project_type}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, project_type: value }))}
                  >
                    <SelectTrigger className="bg-gray-800/50 border-gray-700" data-testid="estimator-project-type">
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700">
                      {PROJECT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Features Required *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-64 overflow-y-auto p-2">
                    {AVAILABLE_FEATURES.map(feature => (
                      <div
                        key={feature.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          formData.features.includes(feature.id)
                            ? 'border-blue-500/50 bg-blue-500/10'
                            : 'border-gray-700 hover:border-gray-600'
                        }`}
                        onClick={() => handleFeatureToggle(feature.id)}
                      >
                        <Checkbox
                          checked={formData.features.includes(feature.id)}
                          onCheckedChange={() => handleFeatureToggle(feature.id)}
                          className="pointer-events-none"
                        />
                        <div className="flex-1">
                          <p className="text-sm text-white">{feature.label}</p>
                          <p className="text-xs text-gray-500">${feature.price}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Project Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe your project requirements..."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-gray-800/50 border-gray-700 min-h-[100px]"
                    data-testid="estimator-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeline">Timeline</Label>
                  <Select
                    value={formData.timeline}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, timeline: value }))}
                  >
                    <SelectTrigger className="bg-gray-800/50 border-gray-700" data-testid="estimator-timeline">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-900 border-gray-700">
                      <SelectItem value="urgent">Urgent (+50%)</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="flexible">Flexible (-10%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Live Preview */}
                <div className="p-4 rounded-lg bg-gray-800/30 border border-gray-700">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Estimated Range</span>
                    <span className="text-2xl font-bold text-gradient font-mono">
                      ${(calculatePreviewPrice() * 0.85).toLocaleString()} - ${(calculatePreviewPrice() * 1.15).toLocaleString()}
                    </span>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                  data-testid="estimator-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Estimate...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate AI Estimate
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Results */}
          <div className="space-y-6">
            {estimate ? (
              <>
                {/* Price Card */}
                <Card className="bg-gradient-to-br from-blue-900/30 to-green-900/30 border-blue-500/20">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-gray-400 text-sm">Estimated Price</p>
                        <p className="text-4xl font-bold text-white font-mono">{estimate.price_range}</p>
                      </div>
                      <DollarSign className="w-8 h-8 text-green-400" />
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-gray-400">
                        <Clock className="w-4 h-4" />
                        {estimate.timeline_estimate}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Features Breakdown */}
                <Card className="bg-gray-900/50 border-white/10">
                  <CardHeader>
                    <CardTitle className="text-lg font-['Outfit']">Features Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {estimate.features_breakdown?.map((feature, i) => (
                        <div key={i} className="flex items-center justify-between p-2 rounded bg-gray-800/30">
                          <span className="text-gray-300">{feature.feature}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`text-xs ${
                              feature.complexity === 'high' ? 'border-red-500/50 text-red-400' : 'border-blue-500/50 text-blue-400'
                            }`}>
                              {feature.complexity}
                            </Badge>
                            <span className="text-green-400 font-mono">${feature.estimated_price}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Design Structure */}
                <Card className="bg-gray-900/50 border-white/10">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-['Outfit']">
                      <FileCode className="w-5 h-5 text-blue-400" />
                      AI-Generated Design Structure
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-invert prose-sm max-w-none">
                      <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700 font-mono text-sm overflow-auto max-h-96">
                        <ReactMarkdown>{estimate.design_structure}</ReactMarkdown>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="bg-gray-900/50 border-white/10 h-full flex items-center justify-center min-h-[400px]">
                <CardContent className="text-center py-12">
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                    <Calculator className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 font-['Outfit']">Ready to Estimate</h3>
                  <p className="text-gray-400 max-w-sm">
                    Fill out the form to get an instant AI-powered estimate with a design structure preview.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
